import { Sparkles, Share2, Hourglass, Bot, Link2 } from 'lucide-react'
import { Button, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import type { SurveySummaryStructured, SurveyStatus } from '@bandos/shared'
import { BackToMindmapCTA } from '../../../../components/common/BackToMindmapCTA'
import {
  Callout,
  CalloutHeader,
  CalloutIcon,
  CalloutTitle,
  CalloutBody,
  CalloutActions,
  CalloutFootnote,
} from '../../../../components/common/synthesisCallout'
import { SurveySummaryCard } from './SurveySummaryCard'

export interface SurveySynthesisTabProps {
  status: SurveyStatus
  responseCount: number
  structured: SurveySummaryStructured | null
  generatedAt: string | null
  aiSummaryResponseCount: number | null
  mindmapId: string
  shareMenuItems: MenuProps['items']
  onSimulate: () => void
  simulating: boolean
  onComplete: () => void
  isCompleting: boolean
}

interface CtaProps {
  shareMenuItems: MenuProps['items']
  onSimulate: () => void
  simulating: boolean
}

function ShareAndSimulateCTAs({ shareMenuItems, onSimulate, simulating }: CtaProps) {
  return (
    <CalloutActions>
      <Dropdown menu={{ items: shareMenuItems }} trigger={['click']} placement="bottomLeft">
        <Button type="primary" icon={<Link2 size={15} />}>
          Share survey
        </Button>
      </Dropdown>
      <Button
        icon={<Bot size={15} />}
        loading={simulating}
        disabled={simulating}
        onClick={onSimulate}
      >
        Simulate responses
      </Button>
    </CalloutActions>
  )
}

function ActiveNoResponsesEmpty(props: CtaProps) {
  return (
    <Callout>
      <CalloutHeader>
        <CalloutIcon>
          <Share2 size={20} />
        </CalloutIcon>
        <CalloutTitle>No findings yet</CalloutTitle>
      </CalloutHeader>
      <CalloutBody>
        Share the survey link to start collecting responses. Once you have at least one, close the
        survey to generate AI-powered findings — a headline, observations, and opportunities pulled
        directly from the data.
      </CalloutBody>
      <ShareAndSimulateCTAs {...props} />
      <CalloutFootnote>
        Generating findings also updates the confidence scores on your mindmap nodes.
      </CalloutFootnote>
    </Callout>
  )
}

interface ReadyEmptyProps {
  count: number
  onComplete: () => void
  isCompleting: boolean
}

function ActiveHasResponsesEmpty({ count, onComplete, isCompleting }: ReadyEmptyProps) {
  return (
    <Callout>
      <CalloutHeader>
        <CalloutIcon>
          <Sparkles size={20} />
        </CalloutIcon>
        <CalloutTitle>Ready to analyze</CalloutTitle>
      </CalloutHeader>
      <CalloutBody>
        You have {count} response{count !== 1 ? 's' : ''}. Close the survey to generate AI-powered
        findings — a headline, observations, and opportunities pulled from the data.
      </CalloutBody>
      <CalloutActions>
        <Button
          type="primary"
          icon={<Sparkles size={15} />}
          loading={isCompleting}
          disabled={isCompleting}
          onClick={onComplete}
        >
          Complete &amp; analyze
        </Button>
      </CalloutActions>
      <CalloutFootnote>
        This will also update the confidence scores on your mindmap nodes.
      </CalloutFootnote>
    </Callout>
  )
}

function SynthesizingEmpty({ count }: { count: number }) {
  return (
    <Callout>
      <CalloutHeader>
        <CalloutIcon>
          <Hourglass size={20} />
        </CalloutIcon>
        <CalloutTitle>Analyzing your responses…</CalloutTitle>
      </CalloutHeader>
      <CalloutBody>
        We're going through {count} response{count !== 1 ? 's' : ''} to pull out a headline,
        observations, and opportunities. This usually takes a few seconds.
      </CalloutBody>
    </Callout>
  )
}

export function SurveySynthesisTab({
  status,
  responseCount,
  structured,
  generatedAt,
  aiSummaryResponseCount,
  mindmapId,
  shareMenuItems,
  onSimulate,
  simulating,
  onComplete,
  isCompleting,
}: SurveySynthesisTabProps) {
  const isClosed = status === 'closed'
  const isSynthesizing = isClosed && structured == null
  const hasSummary = structured != null

  if (hasSummary) {
    return (
      <>
        <SurveySummaryCard
          structured={structured}
          generatedAt={generatedAt}
          responseCount={aiSummaryResponseCount}
          isPending={false}
        />
        <BackToMindmapCTA mindmapId={mindmapId} />
      </>
    )
  }

  if (isSynthesizing) {
    return <SynthesizingEmpty count={responseCount} />
  }

  if (responseCount === 0) {
    return (
      <ActiveNoResponsesEmpty
        shareMenuItems={shareMenuItems}
        onSimulate={onSimulate}
        simulating={simulating}
      />
    )
  }

  return (
    <ActiveHasResponsesEmpty
      count={responseCount}
      onComplete={onComplete}
      isCompleting={isCompleting}
    />
  )
}
